import { filterClasses } from "@swivl-mono/helpers";
import type { ReactElement } from "react";

export interface SegmentedOption<T extends string> {
  value: T;
  label: string;
}

/**
 * Segmented pill toggle with a single active indicator that slides between
 * segments. Shared across the widget Branding editor and the CTA editor preview
 * so both use the same control.
 */
export function SegmentedToggle<T extends string>({
  options,
  value,
  onChange,
  fullWidth = false,
  size = "md"
}: {
  options: SegmentedOption<T>[];
  value: T;
  onChange: (v: T) => void;
  /** Stretch to the container instead of sizing to the labels (CC right panel). */
  fullWidth?: boolean;
  /** `sm` is the same control at filter scale — the History tab's channel filters. */
  size?: "sm" | "md";
}): ReactElement {
  const isSmall = size === "sm";
  const activeIndex = Math.max(
    0,
    options.findIndex((o) => o.value === value)
  );
  return (
    <div
      className={filterClasses(
        "relative auto-cols-fr grid-flow-col items-stretch bg-grey-200",
        isSmall ? "rounded-md p-0.5" : "rounded-lg p-1",
        fullWidth ? "grid w-full" : "inline-grid"
      )}
    >
      {/* Single pill that slides between segments. Width/offset are dynamic (depend on the
          option count + active index), so they go inline. */}
      <span
        aria-hidden
        className={filterClasses(
          "pointer-events-none absolute bg-white shadow-sm transition-transform duration-200 ease-out",
          isSmall ? "top-0.5 bottom-0.5 left-0.5 rounded-sm" : "top-1 bottom-1 left-1 rounded"
        )}
        // The subtracted width is the track's padding on both sides, so the
        // pill lands exactly on a segment at either size.
        style={{ width: `calc((100% - ${isSmall ? "0.25rem" : "0.5rem"}) / ${options.length})`, transform: `translateX(${activeIndex * 100}%)` }}
      />
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(o.value)}
          className={filterClasses(
            "relative z-10 cursor-pointer px-2 transition-colors",
            isSmall ? "body-xs rounded-sm py-0.5" : "body-sm-alt rounded py-1",
            value === o.value ? "text-grey-900" : "text-grey-600 hover:text-grey-900"
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}
